export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Always a translated string — rendered next to the switch, not just for a11y. */
  label: string;
  disabled?: boolean;
  id?: string;
}

/**
 * A single boolean switch — the one place a preference should read as on/off
 * rather than a checkbox. The thumb only ever moves via transform, so it
 * stays cheap under html[data-motion="off"] and costs nothing when disabled.
 */
export function Toggle({ checked, onChange, label, disabled = false, id }: ToggleProps) {
  return (
    <label
      className={[
        'tap-target inline-flex cursor-pointer items-center gap-3',
        disabled ? 'cursor-not-allowed opacity-60' : '',
      ].join(' ')}
    >
      <span
        className={[
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-[var(--dur-fast)]',
          checked ? 'border-accent bg-accent' : 'border-border-strong bg-surface-3',
          'has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
          'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
        ].join(' ')}
      >
        <span
          aria-hidden="true"
          className={[
            'inline-block h-4 w-4 translate-x-1 transform rounded-full border border-border bg-surface shadow-sm transition-transform duration-[var(--dur-fast)]',
            checked ? 'translate-x-[22px]' : 'translate-x-1',
          ].join(' ')}
        />
        <input
          id={id}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="sr-only"
        />
      </span>
      <span className="text-sm text-fg">{label}</span>
    </label>
  );
}
